import { keyframes } from '@chakra-ui/react';

import { SETUP_BLUE, SETUP_GREEN } from './setupProgress/constants';

// (3159) Colours from the TCAP Concept dashboard design, kept in one place so
// the home sections pick a light/dark pair instead of hardcoding values. They
// signal status rather than brand, so they don't follow the partner
// colorScheme.
const AMBER = '#D97706';
const RED = '#DC2626';
const TILE_BLUE = '#51A2DE';

/**
 * @param {boolean} darkMode
 * @returns the dashboard home's colours for the current theme
 */
export const dashboardTokens = (darkMode) => ({
    heading: darkMode ? 'white' : 'gray.900',
    muted: darkMode ? 'gray.400' : 'gray.500',
    healthy: SETUP_GREEN,
    attention: AMBER,
    notificationHover: darkMode ? 'whiteAlpha.100' : 'blackAlpha.50',
    severity: {
        error: {
            accent: RED,
            bg: darkMode ? 'rgba(220, 38, 38, 0.12)' : '#FDECEC',
        },
        warning: {
            accent: AMBER,
            bg: darkMode ? 'rgba(217, 119, 6, 0.12)' : '#FEF6E7',
        },
    },
    tile: {
        filled: {
            bg: TILE_BLUE,
            border: TILE_BLUE,
            value: 'white',
            label: 'whiteAlpha.900',
        },
        empty: {
            bg: darkMode ? 'whiteAlpha.100' : '#F3F4F6',
            border: darkMode ? 'gray.700' : '#E5E7EB',
            value: darkMode ? 'gray.500' : '#9CA3AF',
            label: darkMode ? 'gray.400' : '#6B7280',
        },
    },
    addService: {
        color: SETUP_BLUE,
        border: darkMode ? 'blue.800' : 'blue.300',
        bg: darkMode ? 'rgba(62, 175, 235, 0.06)' : 'blue.50',
        hoverBg: darkMode ? 'rgba(62, 175, 235, 0.12)' : 'blue.100',
    },
    availableRow: {
        bg: darkMode ? 'whiteAlpha.50' : 'gray.50',
        addNowBg: darkMode ? 'rgba(62, 175, 235, 0.12)' : '#E6F6FF',
    },
});

/**
 * (3134) A service section's accent (left bar and border) and
 * header tint, from its status bar colour. Anything unrecognised is grey.
 *
 * @param {string} [color] - green | yellow | red | blue | gray
 * @param {boolean} darkMode
 * @returns {{ accent: string, bg: string }}
 */
export const sectionStatusTokens = (color, darkMode) => {
    switch (color) {
        case 'green':
            return {
                accent: SETUP_GREEN,
                bg: darkMode ? '#1A3D1A' : '#E8F5E7',
            };
        case 'yellow':
            return { accent: '#EAB308', bg: 'rgba(234, 179, 8, 0.1)' };
        case 'red':
            return { accent: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)' };
        case 'blue':
            return { accent: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' };
        default:
            return { accent: '#9CA3AF', bg: 'rgba(107, 114, 128, 0.1)' };
    }
};

// (3157) Soft amber pulse for anything that needs attention: a collapsed
// service section, and the account summary's "Attention required" bell.
const attentionPulseKeyframes = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.55); }
    50% { box-shadow: 0 0 0 6px rgba(234, 179, 8, 0); }
`;

export const attentionPulse = {
    animation: `${attentionPulseKeyframes} 2s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};
